import { map, Observable, take, tap } from 'rxjs';
import { formatDate } from '@angular/common';
import { BookingApiService } from '@app/users-area/api/booking.api.service';
import { BookingSimple } from '@app/utils/models/booking-simple.model';
import { PublicStatusType } from '@app/utils/models/public-status-type.model';
import { effect, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { ServerActionsStore, ServerActionType } from '@ess-front/shared/websockets';
import { BookingReadyServerAction } from '@app/utils/models/booking-ready-server-action.model';
import { TripApiService } from '@shared/api/trip-api.service';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserTripsDomainService {
  readonly isLoading$$: WritableSignal<boolean> = signal<boolean>(true);
  readonly trips$$: WritableSignal<BookingSimple[]> = signal<BookingSimple[]>([]);

  constructor(
    private bookingApiService: BookingApiService,
    private serverActionsStore: ServerActionsStore,
    private tripApiService: TripApiService,
  ) {
    effect(() => {
      const serverAction = this.serverActionsStore.lastServerAction() as BookingReadyServerAction | null;
      untracked(() => {
        this.updateTripIfBookingReadyServerAction(serverAction);
      });
    });
  }

  unloadTrips(): void {
    this.trips$$.set([]);
  }

  loadTrips$(onlyRecent = false): Observable<void> {
    this.isLoading$$.set(true);
    return this.bookingApiService.getTrips$(onlyRecent).pipe(
      tap(trips => this.trips$$.set(trips)),
      map(() => undefined),
      finalize(() => this.isLoading$$.set(false)),
    );
  }

  getPastTrips(trips: BookingSimple[]): BookingSimple[] {
    return trips.filter(trip => this.isPastTrip(trip));
  }

  getFutureTrips(trips: BookingSimple[]): BookingSimple[] {
    return trips.filter(trip => this.isFutureTrip(trip)).reverse();
  }

  getUpcomingTrips(trips: BookingSimple[]): BookingSimple[] {
    return trips.filter(trip => this.isUpcomingTrip(trip)).reverse();
  }

  private updateTripIfBookingReadyServerAction(serverAction: BookingReadyServerAction | null): void {
    if (serverAction?.actionType === ServerActionType.BookingReady) {
      const bookings = this.trips$$();
      const bookingIndex = bookings.findIndex(booking => booking.hash === serverAction.payload.hash);
      if (bookingIndex > -1) {
        this.patchTrip(serverAction, bookings, bookingIndex);
      }
    }
  }

  private patchTrip(serverAction: BookingReadyServerAction, bookings: BookingSimple[], bookingIndex: number): void {
    this.tripApiService
      .getTrip$(serverAction.payload.hash)
      .pipe(
        tap(({ publicStatus }) => {
          const updatedBooking: BookingSimple = {
            ...bookings[bookingIndex],
            canNavigate:
              publicStatus.value !== PublicStatusType.FUTURE && publicStatus.value !== PublicStatusType.BUILDING,
            publicStatus,
          };
          const updatedBookings = [
            ...bookings.slice(0, bookingIndex),
            updatedBooking,
            ...bookings.slice(bookingIndex + 1),
          ];
          this.trips$$.set(updatedBookings);
        }),
        take(1),
      )
      .subscribe();
  }

  private isPastTrip(booking: BookingSimple): boolean {
    return (
      !!booking.endDate &&
      this.getTodayDateString() > booking.endDate &&
      booking.publicStatus.value !== PublicStatusType.FUTURE
    );
  }

  private isUpcomingTrip(booking: BookingSimple): boolean {
    return this.isUpcomingTripWithEndDate(booking) || this.isUpcomingTripWithoutEndDate(booking);
  }

  private isUpcomingTripWithoutEndDate(booking: BookingSimple): boolean {
    return booking.endDate === null && booking.publicStatus.value !== PublicStatusType.FUTURE;
  }

  private isUpcomingTripWithEndDate(booking: BookingSimple): boolean {
    return (
      !!booking.endDate &&
      this.getTodayDateString() <= booking.endDate &&
      booking.publicStatus.value !== PublicStatusType.FUTURE
    );
  }

  private isFutureTrip(booking: BookingSimple): boolean {
    return booking.publicStatus.value === PublicStatusType.FUTURE;
  }

  private getTodayDateString(): string {
    return formatDate(new Date(), 'yyyy-MM-dd', 'en');
  }
}
